import React, { useState } from 'react';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Card } from '../../components/ui/card';
import { Label } from '../../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Plus, Pencil, Trash2, GripVertical, ArrowUp, ArrowDown } from 'lucide-react';

export default function StagesTab({ refData, apiCall, onRefresh }) {
  const stages = refData?.candidate_stages || [];
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingStage, setEditingStage] = useState(null);
  const [form, setForm] = useState({ name: '', color: '#71717a' });
  const [saving, setSaving] = useState(false);

  const openCreate = () => {
    setEditingStage(null);
    setForm({ name: '', color: '#71717a' });
    setDialogOpen(true);
  };

  const openEdit = (stage) => {
    setEditingStage(stage);
    setForm({ name: stage.name, color: stage.color || '#71717a' });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!form.name.trim()) { toast.error('Stage name is required'); return; }
    setSaving(true);
    try {
      if (editingStage) {
        await apiCall('PUT', `/recruitment/stages/${editingStage.stage_id}`, form);
        toast.success('Stage updated');
      } else {
        await apiCall('POST', '/recruitment/stages', form);
        toast.success('Stage created');
      }
      setDialogOpen(false);
      onRefresh();
    } catch (e) { toast.error(e.message); }
    setSaving(false);
  };

  const handleDelete = async (stage) => {
    if (!confirm(`Delete stage "${stage.name}"? This only works if no applications use this stage.`)) return;
    try {
      await apiCall('DELETE', `/recruitment/stages/${stage.stage_id}`);
      toast.success('Stage deleted');
      onRefresh();
    } catch (e) { toast.error(e.message); }
  };

  const handleMove = async (index, direction) => {
    const ids = stages.map(s => s.stage_id);
    const target = index + direction;
    if (target < 0 || target >= ids.length) return;
    [ids[index], ids[target]] = [ids[target], ids[index]];
    try {
      await apiCall('POST', '/recruitment/stages/reorder', { ordered_ids: ids });
      onRefresh();
    } catch (e) { toast.error(e.message); }
  };

  return (
    <div data-testid="stages-tab">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="text-lg font-semibold">Candidate Stages</h2>
          <p className="text-sm text-muted-foreground">Define and order the stages of your hiring pipeline. These appear as columns in the Kanban view.</p>
        </div>
        <Button onClick={openCreate} size="sm" data-testid="create-stage-btn">
          <Plus className="w-4 h-4 mr-1" /> Add Stage
        </Button>
      </div>

      <Card className="overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-[50px]">Order</TableHead>
              <TableHead className="w-[60px]">Color</TableHead>
              <TableHead>Stage Name</TableHead>
              <TableHead className="w-[80px]">Default</TableHead>
              <TableHead className="w-[120px]"></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {stages.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5} className="text-center py-12 text-muted-foreground">
                  No stages configured. They will be auto-seeded on next reload.
                </TableCell>
              </TableRow>
            ) : stages.map((stage, i) => (
              <TableRow key={stage.stage_id} data-testid={`stage-row-${stage.stage_id}`}>
                <TableCell className="text-center">
                  <div className="flex flex-col items-center gap-0.5">
                    <Button variant="ghost" size="icon" className="h-5 w-5" onClick={() => handleMove(i, -1)} disabled={i === 0}>
                      <ArrowUp className="w-3 h-3" />
                    </Button>
                    <span className="text-xs text-muted-foreground">{i + 1}</span>
                    <Button variant="ghost" size="icon" className="h-5 w-5" onClick={() => handleMove(i, 1)} disabled={i === stages.length - 1}>
                      <ArrowDown className="w-3 h-3" />
                    </Button>
                  </div>
                </TableCell>
                <TableCell>
                  <div className="w-6 h-6 rounded-md border" style={{ backgroundColor: stage.color || '#71717a' }} />
                </TableCell>
                <TableCell className="font-medium">{stage.name}</TableCell>
                <TableCell>
                  {stage.is_default ? (
                    <span className="text-xs text-muted-foreground">Default</span>
                  ) : (
                    <span className="text-xs text-blue-600">Custom</span>
                  )}
                </TableCell>
                <TableCell>
                  <div className="flex gap-1">
                    <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => openEdit(stage)} data-testid={`edit-stage-${stage.stage_id}`}>
                      <Pencil className="w-3.5 h-3.5" />
                    </Button>
                    <Button variant="ghost" size="icon" className="h-7 w-7 text-destructive" onClick={() => handleDelete(stage)} data-testid={`delete-stage-${stage.stage_id}`}>
                      <Trash2 className="w-3.5 h-3.5" />
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>{editingStage ? 'Edit Stage' : 'New Stage'}</DialogTitle>
          </DialogHeader>
          <div className="grid gap-4 py-2">
            <div>
              <Label>Name *</Label>
              <Input value={form.name} onChange={e => setForm(p => ({ ...p, name: e.target.value }))}
                placeholder="e.g. Phone Screen" data-testid="stage-name-input" />
            </div>
            <div>
              <Label>Color</Label>
              <div className="flex items-center gap-3">
                <input type="color" value={form.color} onChange={e => setForm(p => ({ ...p, color: e.target.value }))}
                  className="w-10 h-10 rounded cursor-pointer border" data-testid="stage-color-input" />
                <Input value={form.color} onChange={e => setForm(p => ({ ...p, color: e.target.value }))}
                  className="w-28 font-mono text-sm" placeholder="#71717a" />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleSave} disabled={saving} data-testid="save-stage-btn">
              {saving ? 'Saving...' : editingStage ? 'Save Changes' : 'Add Stage'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
